/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';

import { Box, CircularProgress, InputAdornment, Stack } from '@mui/material';
import { useTheme } from '../../styles';
import { ModelListItem } from './ListItem/ModelListItem';
import {
  AutocompleteContainer,
  AutocompleteInput,
  AutocompletePopper,
  AutocompletePopperPortal,
  getInferenceProviderColour,
  isPaidModel,
  isToolModel,
} from './Base';
import { getModels } from '@requests/telnyx';
import { useQuery } from '@tanstack/react-query';
import { ErrorMessage } from './Models';

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  placeholder?: string;
  error?: string;
  portal?: boolean;
}

export const AssistantModel: React.FC<Props> = ({ value, onChange, disabled, placeholder, error, portal }) => {
  const autocompleteRef = useRef(null);
  const theme = useTheme();

  const { data: models = [], isLoading: loading } = useQuery({
    queryKey: ['models'],
    queryFn: getModels,
  });

  const labels = models.map((model) => ({
    id: model.id,
    label: model.id.split('/')?.[1],
    value: model.id,
    metadata: {
      organization: model.organization,
      color: getInferenceProviderColour(theme, model.organization),
      tier: model.tier,
      paid: isPaidModel(model.id),
      json: false,
      tools: isToolModel(model.id),
    },
  }));

  const [open, setOpen] = useState<boolean>(false);

  return (
    <Stack spacing={1}>
      <AutocompleteContainer open={open}>
        <Autocomplete
          value={labels.find((item) => item?.value === value) || null}
          ref={autocompleteRef}
          onChange={(event, newValue) => {
            onChange(event, newValue || { value: '', label: '' });
            setOpen(false);
            if (newValue) (document?.activeElement as HTMLElement)?.blur();
          }}
          disabled={disabled}
          PopperComponent={portal ? AutocompletePopperPortal : AutocompletePopper}
          groupBy={(option) => option?.metadata?.organization}
          renderOption={(props, option) => <ModelListItem {...props} option={option} />}
          autoHighlight
          options={labels}
          disablePortal
          getOptionLabel={(option) => option?.label || ''}
          renderInput={(params) => (
            <AutocompleteInput
              open={open}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              disabled={disabled}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
              startAdornment={
                value &&
                !loading && (
                  <InputAdornment position='start'>
                    <Box
                      component='span'
                      sx={{
                        width: 12,
                        height: 12,
                        flexShrink: 0,
                        borderRadius: '3px',
                        mr: 0.35,
                        ml: 0.31,
                        mt: '0px',
                      }}
                      style={{ backgroundColor: labels.find((label) => label?.value === value)?.metadata?.color }}
                    />
                  </InputAdornment>
                )
              }
            />
          )}
        />
      </AutocompleteContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Stack>
  );
};
